import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=loading, null=anonymous
  const [admin, setAdmin] = useState(undefined);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("m11_token");
    const role = localStorage.getItem("m11_role");
    if (!token) {
      setUser(null);
      setAdmin(null);
      return;
    }
    // 🔒 INSTANT-BOOT: If we have a cached user profile, hydrate UI immediately
    // so a slow /auth/me network call cannot flash a "logged out" screen on
    // app cold-start (major issue reported on Android WebView).
    try {
      const cached = localStorage.getItem("m11_user_cache");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (role === "admin") {
          setAdmin(parsed); setUser(null);
        } else {
          setUser(parsed); setAdmin(null);
        }
      }
    } catch { /* ignore corrupt cache */ }

    try {
      if (role === "admin") {
        const { data } = await api.get("/admin/auth/me");
        setAdmin(data);
        setUser(null);
        localStorage.setItem("m11_user_cache", JSON.stringify(data));
      } else {
        const { data } = await api.get("/auth/me");
        setUser(data);
        setAdmin(null);
        localStorage.setItem("m11_user_cache", JSON.stringify(data));
      }
    } catch (e) {
      const status = e?.response?.status;
      // On 401/403: DO NOT auto-logout on cold-boot with cached profile.
      // Modern users expect Instagram/Zomato style "always logged in".
      // If token is truly bad, backend will 401 on next real action & user re-logs in manually.
      if ((status === 401 || status === 403) && !localStorage.getItem("m11_user_cache")) {
        localStorage.removeItem("m11_token");
        localStorage.removeItem("m11_role");
        localStorage.removeItem("m11_mobile");
        setUser(null);
        setAdmin(null);
        return;
      }
      // Network / 5xx / 401-with-cache: keep the cached session alive
      setUser((prev) => (prev === undefined && role !== "admin") ? { _stale: true } : prev);
      setAdmin((prev) => (prev === undefined && role === "admin") ? { _stale: true } : prev);
    }
  }, []);

  useEffect(() => {
    fetchMe();
    // Auto-refresh user profile when app comes back to foreground
    // (user switched tab, locked screen, swipped out of APK, etc.)
    const onVisible = () => {
      if (document.visibilityState === "visible" && localStorage.getItem("m11_token")) {
        fetchMe();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [fetchMe]);

  const login = async (mobile, password) => {
    const { data } = await api.post("/auth/login", { mobile, password });
    localStorage.setItem("m11_token", data.token);
    localStorage.setItem("m11_role", "user");
    localStorage.setItem("m11_mobile", data.user?.mobile || mobile);
    localStorage.setItem("m11_user_cache", JSON.stringify(data.user));
    // APK bridge: register user with OneSignal for targeted push notifications
    if (typeof window !== "undefined" && window.AndroidBridge?.setUserId) {
      try { window.AndroidBridge.setUserId(String(data.user?.mobile || mobile)); } catch {}
    }
    setUser(data.user);
    setAdmin(null);
    return data.user;
  };

  const register = async (mobile, name, password, referral_code) => {
    const { data } = await api.post("/auth/register", { mobile, name, password, referral_code });
    localStorage.setItem("m11_token", data.token);
    localStorage.setItem("m11_role", "user");
    localStorage.setItem("m11_mobile", data.user?.mobile || mobile);
    localStorage.setItem("m11_user_cache", JSON.stringify(data.user));
    if (typeof window !== "undefined" && window.AndroidBridge?.setUserId) {
      try { window.AndroidBridge.setUserId(String(data.user?.mobile || mobile)); } catch {}
    }
    setUser(data.user);
    return data.user;
  };

  const setSessionFromResetToken = (token, userData) => {
    localStorage.setItem("m11_token", token);
    localStorage.setItem("m11_role", "user");
    localStorage.setItem("m11_user_cache", JSON.stringify(userData));
    setUser(userData);
    setAdmin(null);
  };

  const adminLogin = async (email, password) => {
    const { data } = await api.post("/admin/auth/login", { email, password });
    localStorage.setItem("m11_token", data.token);
    localStorage.setItem("m11_role", "admin");
    localStorage.setItem("m11_user_cache", JSON.stringify(data.admin));
    setAdmin(data.admin);
    setUser(null);
    return data.admin;
  };

  const logout = () => {
    localStorage.removeItem("m11_token");
    localStorage.removeItem("m11_role");
    localStorage.removeItem("m11_mobile");
    localStorage.removeItem("m11_user_cache");
    if (typeof window !== "undefined" && window.AndroidBridge?.clearUserId) {
      try { window.AndroidBridge.clearUserId(); } catch {}
    }
    setUser(null);
    setAdmin(null);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // ignore
    }
  };

  return (
    <AuthContext.Provider value={{ user, admin, login, register, adminLogin, logout, refreshUser, refresh: refreshUser, setSessionFromResetToken, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
